import { supabase } from "../lib/supabase";

/**
 * All google-oauth actions are routed through the single Edge Function.
 * The caller must supply the advertiser_id and an auth token from the session.
 */
async function invoke(action, advertiserId, authToken) {
  if (!supabase) {
    throw new Error("Supabase client is not initialized");
  }
  const { data, error } = await supabase.functions.invoke("google-oauth", {
    body: { action, advertiser_id: advertiserId },
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (error) {
    throw new Error(error.message ?? "google-oauth function error");
  }
  return data;
}

/**
 * Returns the connection status for the given advertiser.
 * Shape: { connected: boolean, provider_email?, selected_property_id?, properties? }
 */
export async function getStatus(advertiserId, authToken) {
  return invoke("status", advertiserId, authToken);
}

/**
 * Returns the Google OAuth authorization URL to redirect the user to.
 * Shape: { url: string }
 * Callers should use window.location.assign(url) to initiate the OAuth flow.
 */
export async function getStartUrl(advertiserId, authToken) {
  if (!supabase) throw new Error("Supabase client is not initialized");
  const { data, error } = await supabase.functions.invoke("google-oauth", {
    body: {
      action: "start",
      advertiser_id: advertiserId,
      return_url: window.location.origin,
    },
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (error) throw new Error(error.message ?? "google-oauth function error");
  return data.url;
}

/**
 * Disconnects the advertiser's Google Analytics integration by deleting
 * tokens and properties from the database.
 * Shape: { success: boolean }
 */
export async function disconnect(advertiserId, authToken) {
  return invoke("disconnect", advertiserId, authToken);
}

/**
 * Fetches a fresh list of GA4 properties from Google and upserts them.
 * Returns the updated property list array.
 */
export async function refreshProperties(advertiserId, authToken) {
  const data = await invoke("refresh-properties", advertiserId, authToken);
  return data.properties;
}
